<template>
  <div class="budget-table w-full border border-[#E0E0E04A] p-6 rounded-xl bg-[#F5F6FAB3]">
    <!-- Header -->
    <div class="grid grid-cols-[40%_15%_15%_15%_15%] mb-1">
      <div class="h-[33px] flex items-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans'] pl-10">NEEDS</div>
      <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">YEAR 1</div>
      <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">YEAR 2</div>
      <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">YEAR 3</div>
      <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">TOTAL</div>
    </div>

    <!-- Categories -->
    <div>
      <div v-for="(category, index) in categoriesWithTotals" :key="index" class="category mb-2">
        <!-- Category Header -->
        <div 
          :class="[
            'grid grid-cols-[40%_15%_15%_15%_15%] items-center h-[33px] cursor-pointer relative bg-white divide-x divide-[#E0E0E04A]',
            category.isOpen ? 'border-b border-[#E0E0E04A] rounded-t-[12px]' : 'rounded-[12px]',
          ]"
          @click="toggleCategory(index)"
        >
          <div class="flex items-center w-full">
            <span class="transform h-6 flex items-center justify-center absolute left-4">
              <svg v-if="category.isOpen" width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 1.75258L0.85 5L0 4.12371L4 0L8 4.12371L7.15 5L4 1.75258Z" fill="#00423D"/>
              </svg>
              <svg v-else width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 3.24742L7.15 4.02528e-07L8 0.876289L4 5L3.60506e-07 0.876289L0.85 -1.48236e-07L4 3.24742Z" fill="#00423D"/>
              </svg>
            </span>
            <div class="pl-10 flex items-center w-full">
              <span class="text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">{{ category.name }}</span>
              <span class="text-[#414141] text-[8px] leading-[105%] uppercase font-['Open_Sans'] font-semibold ml-auto mr-4">(COUNT: {{ category.items.length }})</span>
            </div>
          </div>
          <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">${{ formatNumber(category.totals.year1) }}</div>
          <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">${{ formatNumber(category.totals.year2) }}</div>
          <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans']">${{ formatNumber(category.totals.year3) }}</div>
          <div class="h-[33px] flex items-center justify-center text-[#00423D] font-semibold text-[12px] leading-[105%] uppercase font-['Open_Sans'] bg-[#FAF8F4]">${{ formatNumber(category.totals.total) }}</div>
        </div>

        <!-- Category Items -->
        <div v-if="category.isOpen">
          <div class="divide-y divide-[#E0E0E04A] bg-white overflow-hidden" :class="{ 'rounded-b-[12px]': category.items.length > 0 }">
            <div v-for="(item, itemIndex) in category.items" :key="itemIndex" 
                 class="grid grid-cols-[40%_15%_15%_15%_15%] items-center h-[33px] bg-white divide-x divide-[#E0E0E04A]">
              <div class="flex items-center w-full h-full">
                <div class="pl-10 flex items-center w-full">
                  <input 
                    v-model="item.name"
                    type="text"
                    class="w-full border-none bg-transparent focus:outline-none text-[#414141] text-[12px] leading-[105%] font-['Open_Sans'] font-normal"
                    placeholder="Enter item name..."
                    @input="handleItemChange"
                    @blur="handleItemBlur"
                  />
                  <button @click.stop="deleteItem(index, itemIndex)" class="text-[#851208] ml-auto mr-4">
                    <svg width="17" height="17" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5249 12.4498C2.17428 12.4498 1.87412 12.325 1.62443 12.0753C1.37475 11.8256 1.2499 11.5254 1.2499 11.1748V2.6748H0.399902V1.3998H3.7999V0.549805H7.1999V1.3998H10.5999V2.6748H9.7499V11.1672C9.7499 11.5264 9.62506 11.83 9.37537 12.0779C9.12568 12.3258 8.82553 12.4498 8.4749 12.4498H2.5249ZM8.4749 2.6748H2.5249V11.1748H8.4749V2.6748ZM3.7999 9.89981H5.0749V3.9498H3.7999V9.89981ZM5.9249 9.89981H7.1999V3.9498H5.9249V9.89981Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="h-[33px] flex items-center justify-center">
                <input 
                  v-model.number="item.year1"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  class="w-full text-center border-none bg-transparent focus:outline-none text-[#414141] text-[12px] leading-[105%] font-['Open_Sans'] font-normal"
                  placeholder="0"
                  @keydown.arrow-up.prevent
                  @keydown.arrow-down.prevent
                  @wheel.prevent
                  @input="handleNumberInput($event, item, 'year1')"
                  @blur="handleItemBlur"
                />
              </div>
              <div class="h-[33px] flex items-center justify-center">
                <input 
                  v-model.number="item.year2"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  class="w-full text-center border-none bg-transparent focus:outline-none text-[#414141] text-[12px] leading-[105%] font-['Open_Sans'] font-normal"
                  placeholder="0"
                  @keydown.arrow-up.prevent
                  @keydown.arrow-down.prevent
                  @wheel.prevent
                  @input="handleNumberInput($event, item, 'year2')"
                  @blur="handleItemBlur"
                />
              </div>
              <div class="h-[33px] flex items-center justify-center">
                <input 
                  v-model.number="item.year3"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  class="w-full text-center border-none bg-transparent focus:outline-none text-[#414141] text-[12px] leading-[105%] font-['Open_Sans'] font-normal"
                  placeholder="0"
                  @keydown.arrow-up.prevent
                  @keydown.arrow-down.prevent
                  @wheel.prevent
                  @input="handleNumberInput($event, item, 'year3')"
                  @blur="handleItemBlur"
                />
              </div>
              <div class="h-[33px] flex items-center justify-center text-[#414141] text-[12px] leading-[105%] font-['Open_Sans'] font-normal">${{ formatNumber(calculateItemTotal(item)) }}</div>
            </div>
          </div>
          
          <!-- Add More Button -->
          <div class="pl-10 mb-2">
            <button 
              @click.stop="addItem(index)"
              class="text-[#414141] text-[10px] leading-[105%] uppercase font-['Open_Sans'] font-semibold underline underline-offset-0"
            >
              + ADD MORE
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Project Budget Total -->
    <div class="mt-2 -mx-6 -mb-6 bg-[#00423D] rounded-b-xl w-[calc(100%+48px)]">
      <div class="px-6">
        <div class="grid grid-cols-[40%_15%_15%_15%_15%] h-[33px] items-center">
          <div class="flex items-center w-full">
            <div class="pl-10 flex items-center justify-end w-full">
              <span class="text-white text-[14px] leading-[105%] uppercase font-['Minion_Pro'] font-semibold">{{ budgetTotalLabel }}</span>
            </div>
          </div>
          <div class="h-[33px] flex items-center justify-center text-white text-[18px] leading-[105%] uppercase font-['Minion_Pro'] font-semibold">${{ formatNumber(projectTotal.year1) }}</div>
          <div class="h-[33px] flex items-center justify-center text-white text-[18px] leading-[105%] uppercase font-['Minion_Pro'] font-semibold">${{ formatNumber(projectTotal.year2) }}</div>
          <div class="h-[33px] flex items-center justify-center text-white text-[18px] leading-[105%] uppercase font-['Minion_Pro'] font-semibold">${{ formatNumber(projectTotal.year3) }}</div>
          <div class="h-[33px] flex items-center justify-center text-white text-[18px] leading-[105%] uppercase font-['Minion_Pro'] font-semibold">${{ formatNumber(projectTotal.total) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { safeSum, roundTwo } from '@/composables/numberUtils';

const props = defineProps({
  categories: {
    type: Array,
    required: true
  },
  total_programs_budget: {
    type: Number,
    default: 0
  },
  budgetTotalLabel: {
    type: String,
    default: 'PROJECT BUDGET TOTAL'
  }
});

const emit = defineEmits(['next', 'back', 'delete-item', 'data-changed']);

// Auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 1000; // 1 second delay

// Calculate total for a single item
const calculateItemTotal = (item) => {
  return safeSum(item.year1 || 0, item.year2 || 0, item.year3 || 0);
};

// Calculate totals for each category
const calculateCategoryTotals = (items) => {
  return items.reduce((acc, item) => {
    acc.year1 = safeSum(acc.year1, item.year1);
    acc.year2 = safeSum(acc.year2, item.year2);
    acc.year3 = safeSum(acc.year3, item.year3);
    acc.total = safeSum(acc.year1, acc.year2, acc.year3);
    return acc;
  }, { year1: 0, year2: 0, year3: 0, total: 0 });
};

// Computed property to add totals to categories
const categoriesWithTotals = computed(() => {
  return props.categories.map(category => ({
    ...category,
    totals: calculateCategoryTotals(category.items)
  }));
});

// Calculate project total
const projectTotal = computed(() => {
  return categoriesWithTotals.value.reduce((acc, category) => {
    acc.year1 = safeSum(acc.year1, category.totals.year1);
    acc.year2 = safeSum(acc.year2, category.totals.year2);
    acc.year3 = safeSum(acc.year3, category.totals.year3);
    acc.total = safeSum(acc.year1, acc.year2, acc.year3);
    return acc;
  }, { year1: 0, year2: 0, year3: 0, total: 0 });
});

// Format numbers with commas; hide decimals when value is integer
const formatNumber = (num) => {
  const n = roundTwo(num || 0);
  if (Number.isInteger(n)) {
    return n.toLocaleString('en-US');
  }
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// Toggle category open/close
const toggleCategory = (index) => {
  props.categories[index].isOpen = !props.categories[index].isOpen;
};

// Add new item to category
const addItem = (categoryIndex) => {
  const newItem = {
    name: '',
    year1: 0,
    year2: 0,
    year3: 0
  };
  
  props.categories[categoryIndex].items.push(newItem);
  
  // Trigger auto-save when adding new item
  triggerAutoSave();
};

// Delete item from category
const deleteItem = (categoryIndex, itemIndex) => {
  emit('delete-item', categoryIndex, itemIndex);
  
  // Trigger auto-save when deleting item
  triggerAutoSave();
};

// Handle text input changes
const handleItemChange = () => {
  triggerAutoSave();
};

// Handle number input changes (limit to 2 decimal places)
const handleNumberInput = (event, item, field) => {
  let raw = event.target.value;
  if (raw === '' || raw === null) {
    item[field] = 0;
    triggerAutoSave();
    return;
  }

  // Truncate fractional part to 2 digits if present
  const parts = String(raw).split('.');
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2);
    raw = parts[0] + '.' + parts[1];
    event.target.value = raw;
  }

  let value = parseFloat(String(raw).replace(/,/g, '')) || 0;
  if (value < 0) {
    value = Math.abs(value);
    event.target.value = String(value);
  }

  // Store rounded to 2 decimals
  item[field] = Math.round(value * 100) / 100;

  // Trigger auto-save
  triggerAutoSave();
};

// Handle input blur events
const handleItemBlur = () => {
  // Save immediately on blur
  clearTimeout(autoSaveTimer);
  triggerAutoSave(true);
};

// Trigger auto-save with debouncing
const triggerAutoSave = (immediate = false) => {
  // Clear existing timer
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  
  if (immediate) {
    // Save immediately
    emit('data-changed', immediate);
  } else {
    // Debounce the save
    autoSaveTimer = setTimeout(() => {
      emit('data-changed', false);
    }, AUTO_SAVE_DELAY);
  }
};

// Watch for external changes to categories (e.g., from API)
watch(() => props.categories, () => {
  // Clear any pending auto-save when categories are updated externally
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
}, { deep: true });

// Navigation functions
const onNextClick = () => {
  emit('next');
};

const onBackClick = () => {
  emit('back');
};

// Prevent negative values (keeping for backward compatibility)
const preventNegative = (event, item, field) => {
  const value = Number(event.target.value);
  if (value < 0) {
    event.target.value = Math.abs(value).toString();
  }
  item[field] = value;
};
</script>

<style scoped>
.budget-table {
  @apply rounded-lg p-6 relative;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style> 